import React, { useEffect, useState } from 'react';
import { Calendar, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const ClientAccount = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const { data } = await api.get('/bookings/my');
      setBookings(data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const upcomingBookings = bookings.filter(
    (b) => b.status === 'confirmed' && new Date(b.date) >= new Date()
  );
  const pastBookings = bookings.filter((b) => new Date(b.date) < new Date());

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-serif font-bold text-primary mb-2">Mijn Account</h1>
          <p className="text-muted-foreground">Welkom terug, {user?.name}!</p>
        </div>

        {/* Upcoming Bookings */}
        <section className="mb-12">
          <h2 className="text-2xl font-serif font-semibold text-primary mb-6">
            Aankomende Afspraken
          </h2>
          {upcomingBookings.length === 0 ? (
            <div className="bg-card border border-border rounded-sm p-8 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                U heeft geen aankomende afspraken.
              </p>
              <a
                href="/book"
                className="text-primary font-medium hover:underline"
              >
                Boek nu een afspraak
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="bg-card border border-border rounded-sm p-6"
                  data-testid="upcoming-booking"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-green-600">Bevestigd</span>
                      </div>
                      {booking.promo?.title && (
                        <span className="inline-block bg-[#c5a059]/20 text-[#0d3b25] text-[10px] px-3 py-0.5 rounded-full font-bold tracking-wider">
                          BINGO OFFER: {booking.promo.title}
                        </span>
                      )}
                      <h3 className="text-lg font-semibold text-primary">
                        {(booking.services && booking.services.length > 0)
                          ? booking.services.map((s) => s.name).join(', ')
                          : `${booking.service_ids.length} Dienst(en)`}
                      </h3>
                      {booking.staff?.name && (
                        <p className="text-xs text-[#c5a059]">
                          ✂️ Met {booking.staff.name}{booking.staff.role ? ` — ${booking.staff.role}` : ''}
                        </p>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(booking.date).toLocaleDateString('nl-NL')}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>{booking.time}</span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Totale prijs: €{booking.total_price.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Past Bookings */}
        <section>
          <h2 className="text-2xl font-serif font-semibold text-primary mb-6">
            Eerdere Afspraken
          </h2>
          {pastBookings.length === 0 ? (
            <div className="bg-card border border-border rounded-sm p-8 text-center">
              <p className="text-muted-foreground">Geen eerdere afspraken gevonden.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pastBookings.slice(0, 5).map((booking) => (
                <div
                  key={booking.id}
                  className="bg-card border border-border rounded-sm p-6 opacity-75"
                  data-testid="past-booking"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-primary">
                        {booking.service_ids.length} Dienst(en)
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(booking.date).toLocaleDateString('nl-NL')}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>{booking.time}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ClientAccount;
